import { useQuery } from "@tanstack/react-query";
import { isMock, getJson } from "./client";

export interface EfficacyPoint {
  month: string;
  segment: string;
  metricName: string;
  loanCount: number | null;
  nEvents: number | null;
  eventRate: number | null;
  predMean: number | null;
  auc: number | null;
  ks: number | null;
  brier: number | null;
  aucMomDelta: number | null;
  ksMomDelta: number | null;
  brierMomDelta: number | null;
  status: string | null;
}

export interface EfficacyResponse {
  points: EfficacyPoint[];
}

export interface EfficacyVintagePoint {
  segment: string;
  metricName: string;
  month: string;
  year: number;
  monthNum: number;
  monthOnBook: number;
  auc: number | null;
  ks: number | null;
  brier: number | null;
}

export interface EfficacyVintageResponse {
  points: EfficacyVintagePoint[];
}

export const SEGMENT_COLORS: Record<string, string> = {
  OVERALL: "#22d3ee",
  PG:      "#60a5fa",
  CORP:    "#a78bfa",
};

// ── Mock ─────────────────────────────────────────────────────────────────────

function seededRand(seed: number) {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const MONTHS: string[] = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(2024, i, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
});

const SEGMENT_BASES: Record<string, { auc: number; ks: number; brier: number; eventRate: number; predMean: number; loans: number }> = {
  OVERALL: { auc: 0.695, ks: 0.315, brier: 0.038, eventRate: 0.042, predMean: 0.031, loans: 4800 },
  PG:      { auc: 0.710, ks: 0.330, brier: 0.034, eventRate: 0.038, predMean: 0.028, loans: 3200 },
  CORP:    { auc: 0.668, ks: 0.288, brier: 0.044, eventRate: 0.051, predMean: 0.039, loans: 1600 },
};

function buildMockPoints(): EfficacyPoint[] {
  const points: EfficacyPoint[] = [];
  for (const segment of ["OVERALL", "PG", "CORP"]) {
    const base = SEGMENT_BASES[segment];
    const rand = seededRand(segment.charCodeAt(0) * 7);
    let prevAuc: number | null = null, prevKs: number | null = null, prevBrier: number | null = null;
    for (const month of MONTHS) {
      const drift = (parseInt(month.slice(5), 10) - 1) * 0.003;
      const auc   = +(base.auc   - drift + (rand() - 0.5) * 0.02).toFixed(4);
      const ks    = +(base.ks    - drift + (rand() - 0.5) * 0.02).toFixed(4);
      const brier = +(base.brier + drift * 0.5 + (rand() - 0.5) * 0.005).toFixed(6);
      const eventRate = +(base.eventRate + (rand() - 0.5) * 0.008).toFixed(6);
      const predMean  = +(base.predMean  + (rand() - 0.5) * 0.005).toFixed(6);
      const loans = base.loans + Math.round((rand() - 0.5) * 400);
      points.push({
        month,
        segment,
        metricName: "delinquency",
        loanCount: loans,
        nEvents: Math.round(eventRate * loans),
        eventRate,
        predMean,
        auc,
        ks,
        brier,
        aucMomDelta:   prevAuc   !== null ? +(auc   - prevAuc).toFixed(4)   : null,
        ksMomDelta:    prevKs    !== null ? +(ks    - prevKs).toFixed(4)    : null,
        brierMomDelta: prevBrier !== null ? +(brier - prevBrier).toFixed(6) : null,
        status: auc >= 0.65 ? "PASS" : "WARN",
      });
      prevAuc = auc; prevKs = ks; prevBrier = brier;
    }
  }
  return points;
}

const MOCK: EfficacyResponse = { points: buildMockPoints() };

export async function fetchEfficacy(): Promise<EfficacyResponse> {
  if (isMock) return Promise.resolve(MOCK);
  return getJson<EfficacyResponse>("model-drift-monitoring/efficacy?model_id=2");
}

export function useEfficacy() {
  return useQuery({ queryKey: ["efficacy"], queryFn: fetchEfficacy });
}

function buildMockVintagePoints(): EfficacyVintagePoint[] {
  const pts: EfficacyVintagePoint[] = [];
  const years = [2021, 2022, 2023, 2024];
  const segments = ["OVERALL", "PG", "CORP"];
  const statuses = ["delinquency", "default"];
  for (const segment of segments) {
    const base = SEGMENT_BASES[segment];
    for (const status of statuses) {
      for (const year of years) {
        const maxMob = 2024 - year === 0 ? 12 : Math.min(48, (2024 - year) * 12);
        const rand = seededRand(segment.charCodeAt(0) * 13 + year + status.length);
        for (let mob = 1; mob <= maxMob; mob++) {
          const drift = mob * 0.001;
          const adjBase = status === "default" ? base.auc - 0.03 : base.auc;
          const absMonth = (year - 2021) * 12 + mob;
          const calYear = 2021 + Math.floor((absMonth - 1) / 12);
          const calMo = ((absMonth - 1) % 12) + 1;
          const month = `${calYear}-${String(calMo).padStart(2, "0")}`;
          pts.push({
            segment,
            metricName: status,
            month,
            year,
            monthNum: calMo,
            monthOnBook: mob,
            auc: +(adjBase - drift + (rand() - 0.5) * 0.02).toFixed(4),
            ks: +(base.ks - drift * 0.5 + (rand() - 0.5) * 0.015).toFixed(4),
            brier: +(base.brier + drift * 0.3 + (rand() - 0.5) * 0.004).toFixed(6),
          });
        }
      }
    }
  }
  return pts;
}

const MOCK_VINTAGE: EfficacyVintageResponse = { points: buildMockVintagePoints() };

export async function fetchEfficacyVintage(): Promise<EfficacyVintageResponse> {
  if (isMock) return Promise.resolve(MOCK_VINTAGE);
  return getJson<EfficacyVintageResponse>("model-drift-monitoring/efficacy-vintage?model_id=2");
}

export function useEfficacyVintage() {
  return useQuery({ queryKey: ["efficacy-vintage"], queryFn: fetchEfficacyVintage });
}
